import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/** Admin-only sign-in (staff panel). Public customers do not need an account. */
const AdminLogin = ({ redirectTo = "/auth/secure-gate" }) => {
    const { user, login, logout, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!authLoading && user?.role === "admin") {
            navigate(redirectTo, { replace: true });
        }
    }, [user, authLoading, navigate, redirectTo]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(form.email, form.password);
            if (u.role !== "admin") {
                logout();
                toast.error("Admin access only. Use your staff credentials.");
                return;
            }
            toast.success("Welcome back");
            navigate(redirectTo, { replace: true });
        } catch (err) {
            const msg = err.response?.data?.detail || "Invalid email or password";
            toast.error(typeof msg === "string" ? msg : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#fafafa] text-[#7a7a84]">
                Loading…
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#fafafa] px-4" data-testid="admin-login-page">
            <div className="w-full max-w-md bike-card p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-[#ED1C24] flex items-center justify-center text-white font-display font-black">S</div>
                    <div>
                        <p className="font-display font-bold text-lg leading-tight">Servall Admin</p>
                        <p className="text-xs text-[#7a7a84]">Staff sign-in only</p>
                    </div>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="label">Email</label>
                        <input
                            type="email"
                            className="input"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            required
                            autoComplete="username"
                            data-testid="auth-email"
                        />
                    </div>
                    <div>
                        <label className="label">Password</label>
                        <input
                            type="password"
                            className="input"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            required
                            autoComplete="current-password"
                            data-testid="auth-password"
                        />
                    </div>
                    <button type="submit" disabled={loading} className="btn btn-primary w-full justify-center" data-testid="auth-submit">
                        {loading ? "…" : "Sign in"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AdminLogin;
